import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Bell, FileText, Settings, LogOut,
  Users, Menu, X, BarChart3, Shield
} from 'lucide-react';
import { RoleBadge } from './UI';

export default function Layout() {
  const { user, logout, canManageUsers, isManager, canManageReports } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  function handleLogout() { logout(); navigate('/login'); }

  const NAV = [
    { to: '/', label: 'Dashboard', Icon: LayoutDashboard, end: true, show: true },
    { to: '/reminders', label: 'Reports', Icon: Bell, show: true },
    { to: '/analytics', label: 'Analytics', Icon: BarChart3, show: isManager },
    { to: '/logs', label: 'Email Logs', Icon: FileText, show: canManageReports },
    { to: '/users', label: 'Users', Icon: Users, show: canManageUsers },
    { to: '/audit', label: 'Audit Logs', Icon: Shield, show: canManageUsers },
    { to: '/settings', label: 'Settings', Icon: Settings, show: true },
  ].filter(n => n.show);

  const initials = (user?.full_name || user?.username || '?').slice(0, 2).toUpperCase();

  const Sidebar = () => (
    <aside style={{
      width: 'var(--sidebar-width)', flexShrink: 0, background: 'var(--primary-dark)',
      display: 'flex', flexDirection: 'column', height: '100vh',
      position: 'sticky', top: 0, zIndex: 50, overflowY: 'auto',
    }}>
      {/* Logo */}
      <div style={{ padding: '22px 20px 18px', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Bell size={18} color="#fff" />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#fff', letterSpacing: '-0.02em' }}>Compliance</div>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,.5)', letterSpacing: '.08em', textTransform: 'uppercase', fontWeight: 600 }}>Report Reminders</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV.map(({ to, label, Icon, end }) => (
          <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
              borderRadius: 'var(--radius-sm)', textDecoration: 'none', fontSize: 13.5,
              fontWeight: 600, transition: 'all .15s',
              color: isActive ? '#fff' : 'rgba(255,255,255,.6)',
              background: isActive ? 'rgba(255,255,255,.12)' : 'transparent',
            })}
            onMouseEnter={e => { const a = e.currentTarget; if (!a.getAttribute('aria-current')) a.style.background = 'rgba(255,255,255,.07)'; }}
            onMouseLeave={e => { const a = e.currentTarget; if (!a.getAttribute('aria-current')) a.style.background = 'transparent'; }}
          >
            <Icon size={16} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* User info */}
      <div style={{ padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.full_name || user?.username}</div>
            <RoleBadge role={user?.role} />
          </div>
        </div>
        <button onClick={handleLogout} style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px',
          background: 'rgba(255,255,255,.07)', border: 'none', borderRadius: 8, cursor: 'pointer',
          color: 'rgba(255,255,255,.7)', fontSize: 12.5, fontWeight: 600, transition: 'all .15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.13)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,.07)'}
        >
          <LogOut size={14} /> Sign Out
        </button>
      </div>
    </aside>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile overlay */}
      {open && <div className="sidebar-overlay" onClick={() => setOpen(false)} />}

      {/* Sidebar desktop */}
      <div className="hide-mobile">
        <Sidebar />
      </div>

      {/* Sidebar mobile */}
      {open && (
        <div style={{ position: 'fixed', top: 0, left: 0, zIndex: 50 }}>
          <Sidebar />
          <button onClick={() => setOpen(false)} style={{ position: 'absolute', top: 16, right: -44, background: 'rgba(0,0,0,.5)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={18} color="#fff" />
          </button>
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Mobile topbar */}
        <div style={{ display: 'none', padding: '12px 16px', background: 'var(--primary-dark)', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 40 }} className="mobile-topbar">
          <button onClick={() => setOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff' }}><Menu size={20} /></button>
          <span style={{ fontWeight: 800, color: '#fff', fontSize: 15 }}>Compliance Reminders</span>
        </div>
        <main style={{ flex: 1 }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
